import * as React from 'react';
import { LaunchResourceOptions } from 'Environment';
import { SendLogoffMessage } from 'Environment/ChromeApp/ChromeAppBridge';
import { AuthenticationTimerOperationType } from 'Environment/ChromeApp/types';
import { TroubleshootPayload } from 'Environment/launchResource/chromeApp';
import { LeasingProperties } from 'Workspace/ChromeApp/ChromeAppCustomerConfiguration';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export interface PlatformInfo {
	appVersion?: string;
	buildVersion?: string;
	osVersion?: string;
	deviceId?: string;
	osName?: string;
}

export interface State {
	storeInfoSent?: boolean;
	storeHealthyForLaunch?: boolean;
	leasingProperties?: LeasingProperties;
	troubleshootQueue: PendingTroubleshoot[];
}

export interface ChromeAppConfigurationState {
	isActive: boolean;
	allowAccessToOfflineResources: boolean;
	useAuthenticationTimer: boolean;
	sessionReconnect?: boolean;
	inspectConnection?: boolean;
	clipboardSupport?: boolean;
	blobDownloadSupport?: boolean;
	platform?: PlatformInfo;
	supportsGetAndResetShieldStateInformationForAnalytics?: boolean;
	getShieldInformation?: boolean;
}

export interface PendingTroubleshoot {
	payload: TroubleshootPayload;
}

export interface ChromeAppContext {
	isSupportedCustomerRuntime?: boolean;

	getChromeAppConfiguration(): Promise<ChromeAppConfigurationState>;

	shouldChromeAppLaunchResource(resource: Resource): Promise<boolean>;

	noteLogout(params: SendLogoffMessage): Promise<void>;

	launch(launchResourceOptions: LaunchResourceOptions): Promise<any>;
	leasingProperties: LeasingProperties;
	notifyAuthenticationTimer(operation: AuthenticationTimerOperationType): Promise<void>;
	updateChromeAppContext: (partialState: Partial<State>) => void;
	troubleshootQueue: PendingTroubleshoot[];
	clearPendingTroubleshoot(): void;
}

export const defaultContext: ChromeAppContext = {
	isSupportedCustomerRuntime: false,
	getChromeAppConfiguration: () =>
		Promise.resolve({
			isActive: false,
			allowAccessToOfflineResources: false,
			useAuthenticationTimer: false,
			sessionReconnect: false,
			inspectConnection: false,
			clipboardSupport: false,
			blobDownloadSupport: false,
			platform: {},
			supportsGetAndResetShieldStateInformationForAnalytics: false,
			getShieldInformation: false,
		}),
	shouldChromeAppLaunchResource: () => Promise.resolve(false),
	launch: () =>
		Promise.reject(new Error('Use of chrome app context is improperly gated')),
	noteLogout: () => Promise.resolve(),
	leasingProperties: null,
	notifyAuthenticationTimer: () => Promise.resolve(),
	updateChromeAppContext: () => {},
	troubleshootQueue: [],
	clearPendingTroubleshoot: () => {},
};

export const ResiliencyChromeAppContext =
	React.createContext<ChromeAppContext>(defaultContext);
